import React, { useState } from "react";
import { X, Sparkles, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const INDUSTRIES = [
  ["banking", "Banking & Financial Services"], ["insurance", "Insurance"], ["healthcare", "Healthcare & Hospitals"],
  ["government", "Government & Public Sector"], ["energy", "Energy / Oil & Gas"], ["manufacturing", "Manufacturing & Industrial"],
  ["telecom", "Telecom & ICT"], ["cloud", "Cloud / Data Centre"], ["cyber", "Cybersecurity & SOC"],
  ["legal", "Legal / Judiciary"], ["transport", "Transport & Logistics"], ["retail", "Retail & E-Commerce"],
  ["realestate", "Real Estate & Construction"], ["education", "Education & Universities"], ["tourism", "Tourism / Hospitality"],
  ["agriculture", "Agriculture & Food Security"], ["utilities", "Utilities / Water"], ["hr", "Human Resources"],
  ["procurement", "Procurement / Supply Chain"], ["corpfin", "Corporate Finance / Audit"],
];

export default function AddUseCaseModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name_en: "", industry: "banking", business_problem: "", ai_type: "Predictive ML", data_classification: "Internal", business_owner: "AI Governance Office", risk_level: "Medium" });
  const [suggest, setSuggest] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const getSuggestions = async () => {
    const { data } = await api.post("/usecases/suggest", form);
    setSuggest(data);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/usecases", form);
      toast.success(`Use case ${data.id} created (synthetic)`);
      onCreated(data);
    } catch (e) { toast.error("Failed to create use case"); }
    finally { setLoading(false); }
  };

  const Input = ({ label, k, type = "text" }) => (
    <div>
      <label className="mb-1 block text-xs font-semibold text-slate-600">{label}</label>
      <input data-testid={`uc-${k}`} type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} required
        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[var(--qatar)]" />
    </div>
  );
  const Sel = ({ label, k, options }) => (
    <div>
      <label className="mb-1 block text-xs font-semibold text-slate-600">{label}</label>
      <select data-testid={`uc-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)}
        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[var(--qatar)]">
        {options.map((o) => <option key={Array.isArray(o) ? o[0] : o} value={Array.isArray(o) ? o[0] : o}>{Array.isArray(o) ? o[1] : o}</option>)}
      </select>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div data-testid="add-usecase-modal" className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 card-shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold font-head">Add Use Case (Sandbox)</h3>
          <button data-testid="uc-close" onClick={onClose} className="rounded-lg p-1.5 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <Input label="Use Case Name" k="name_en" />
          <div className="grid grid-cols-2 gap-3">
            <Sel label="Industry" k="industry" options={INDUSTRIES} />
            <Sel label="AI Type" k="ai_type" options={["Predictive ML", "Generative AI", "Computer Vision", "NLP", "Forecasting", "Agentic AI"]} />
            <Sel label="Data Classification" k="data_classification" options={["Public", "Internal", "Confidential", "Restricted"]} />
            <Sel label="Risk Level" k="risk_level" options={["Low", "Medium", "High", "Critical"]} />
          </div>
          <Input label="Business Owner" k="business_owner" />
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">Business Problem</label>
            <textarea data-testid="uc-business_problem" value={form.business_problem} onChange={(e) => set("business_problem", e.target.value)} rows={2}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[var(--qatar)]" />
          </div>

          <button type="button" data-testid="uc-suggest" onClick={getSuggestions} className="inline-flex items-center gap-2 rounded-lg border border-[var(--qatar-border)] bg-[var(--qatar-subtle)] px-3 py-2 text-sm font-medium text-[var(--qatar)]">
            <Sparkles className="h-4 w-4" /> Suggest Regulations, Controls & Gates
          </button>
          {suggest && (
            <div data-testid="uc-suggestions" className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs">
              <p className="mb-1"><b>Applicable Qatar Regulations:</b> {suggest.regulations.join(", ")}</p>
              <p className="mb-1"><b>Recommended Controls:</b> {suggest.controls.join(", ")}</p>
              <p className="mb-1"><b>Policies:</b> {suggest.policies.join(", ")}</p>
              <p className="mb-1"><b>Required Evidence:</b> {suggest.evidence.join(", ")}</p>
              <p><b>Red-Team Tests:</b> {suggest.redteam.join(", ")}</p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium">Cancel</button>
            <button data-testid="uc-create" type="submit" disabled={loading} className="qatar-gradient inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
